import React, { useState } from 'react';
import { Search, Menu, TrendingUp, Bell, Sun, Moon } from 'lucide-react';

interface HeaderProps {
  onSearch?: (query: string) => void;
  onMenuToggle?: () => void;
  searchQuery?: string;
}

export const Header: React.FC<HeaderProps> = ({ 
  onSearch, 
  onMenuToggle, 
  searchQuery = '' 
}) => {
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [query, setQuery] = useState(searchQuery);
  const [isDark, setIsDark] = useState(false);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (onSearch) {
      onSearch(query);
    }
    setIsSearchOpen(false);
  };

  return (
    <header className="bg-obsidian sticky top-0 left-0 right-0 z-50 w-full shadow-sm border-b border-white/5">
      <div className="max-w-[1920px] mx-auto px-4 sm:px-6 lg:px-8 w-full">
        <div className="flex justify-between items-center h-16 sm:h-20">
          {/* Logo and Brand */}
          <div className="flex items-center flex-shrink-0">
            <button
              onClick={onMenuToggle}
              className="md:hidden p-2 rounded-xl text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-colors"
            >
              <Menu className="h-6 w-6" />
            </button>
            <div className="flex items-center ml-2 md:ml-0 gap-3">
              <div className="p-2.5 bg-gradient-to-br from-emerald to-mint rounded-2xl shadow-lg">
                <TrendingUp className="h-7 w-7 text-obsidian" />
              </div>
              <div>
                <h1 className="text-xl sm:text-2xl font-black bg-gradient-to-r from-emerald to-mint bg-clip-text text-transparent tracking-tight whitespace-nowrap">
                  stockRecos
                </h1>
                <p className="text-xs text-muted-gray hidden sm:block font-medium text-muted-gray/80">
                  AI-powered by TN Ai
                </p>
              </div>
            </div>
          </div>

          {/* Desktop Search */}
          <div className="hidden md:flex flex-1 max-w-2xl mx-8">
            <form onSubmit={handleSearch} className="w-full">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <Search className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search stocks, companies, sectors..."
                  className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-sm font-medium placeholder-gray-400 hover:border-gray-400"
                />
              </div>
            </form>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2 sm:gap-3">
            {/* Mobile Search Toggle */}
            <button
              onClick={() => setIsSearchOpen(!isSearchOpen)}
              className="md:hidden p-2 rounded-xl text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-colors"
            >
              <Search className="h-6 w-6" />
            </button>

            {/* Live Status Badge */}
            <div className="hidden bg-obsidian lg:flex items-center gap-2 px-4 py-2 rounded-xl border border-white/20">
              <div className="relative">
                <div className="h-2 w-2 bg-green-500 rounded-full animate-pulse" />
                <div className="absolute inset-0 h-2 w-2 bg-green-500 rounded-full animate-ping" />
              </div>
              <div className="text-left">
                <div className="text-xs font-bold text-green-700">Live Data</div>
                <div className="text-[10px] text-green-600">Updated Hourly</div>
              </div>
            </div>

            {/* Notifications */}
            <button className="hidden sm:flex p-2.5 rounded-xl text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-colors relative">
              <Bell className="h-5 w-5" />
              <span className="absolute top-1 right-1 h-2 w-2 bg-red-500 rounded-full" />
            </button>

            {/* Theme Toggle */}
            <button
              onClick={() => setIsDark(!isDark)}
              className="hidden sm:flex p-2.5 rounded-xl text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-colors"
            >
              {isDark ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
            </button>
          </div>
        </div>

        {/* Mobile Search */}
        {isSearchOpen && (
          <div className="md:hidden pb-4 pt-2">
            <form onSubmit={handleSearch}>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <Search className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search stocks, companies..."
                  className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-sm"
                  autoFocus
                />
              </div>
            </form>
          </div>
        )}
      </div>
    </header>
  );
};